/**
 * Public certificate verification page — no authentication required.
 * Shows only: valid/invalid, employee name, course, issue date, cert ID, version.
 * No wider profile/PII surface.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const LANG_LABELS = {
  no: {
    kicker: "Verifikasjon",
    valid: "Dette kursbeviset er gyldig",
    invalid: "Ukjent eller ugyldig kursbevis",
    ourFail: "Kunne ikke verifisere. Prøv igjen om et øyeblikk.",
    employee: "Ansatt",
    course: "Kurs",
    issued: "Utstedt",
    id: "Sertifikat-ID",
    version: "Versjon",
    result: "Resultat",
    supersededBadge: "Erstattet av senere versjon",
    disclaimer: "Dette er et internt Doserne Academy-opplæringsbevis. Det er ikke en offentlig godkjenning.",
  },
};

export default function DoserneCertificateVerify() {
  const { certNumber } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    axios
      .get(`${API}/doserne/verify/${encodeURIComponent(certNumber)}`)
      .then(({ data }) => setState({ loading: false, data, error: false }))
      .catch(() => setState({ loading: false, data: null, error: true }));
  }, [certNumber]);

  const t = LANG_LABELS.no; // Public page uses NO by default (Doserne is a Norwegian company); safe for scanning workers.

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (state.error) {
    return (
      <VerifyShell tone="warn" icon={<ShieldAlert />} kicker={t.kicker} title={t.ourFail} />
    );
  }

  const d = state.data || {};
  const valid = !!d.valid;
  const scorePct = d.score != null ? Math.round(d.score * 100) : null;
  const issued = d.issued_at ? new Date(d.issued_at).toLocaleDateString("no-NO") : "—";
  const employee = d.employee_name || "—";
  const course = typeof d.course_title === "object" && d.course_title !== null
    ? (d.course_title.no || d.course_title.en || Object.values(d.course_title)[0] || "—")
    : d.course_title || "—";

  return (
    <VerifyShell
      tone={valid ? "ok" : "bad"}
      icon={valid ? <ShieldCheck /> : <ShieldAlert />}
      kicker={t.kicker}
      title={valid ? t.valid : t.invalid}
    >
      {valid && (
        <>
          {d.superseded && (
            <div className="inline-block text-[11px] font-bold px-3 py-1 rounded-full bg-amber-100 text-amber-700 uppercase tracking-widest mb-6" data-testid="verify-superseded">
              {t.supersededBadge}
            </div>
          )}
          <dl className="space-y-4">
            <Row label={t.employee} value={employee} />
            <Row label={t.course} value={course} />
            <Row label={t.issued} value={issued} />
            <Row label={t.id} value={d.cert_number} mono />
            <Row label={t.version} value={d.course_version || "—"} mono />
            {scorePct != null && <Row label={t.result} value={`${scorePct}%`} mono />}
          </dl>
          <p className="mt-8 text-xs text-slate-400 leading-relaxed">{t.disclaimer}</p>
        </>
      )}
    </VerifyShell>
  );
}

function VerifyShell({ tone, icon, kicker, title, children }) {
  const ring =
    tone === "ok" ? "bg-emerald-100 text-emerald-600" :
    tone === "warn" ? "bg-amber-100 text-amber-600" :
    "bg-red-100 text-red-600";
  return (
    <div className="min-h-screen bg-slate-50 py-16 px-4" data-testid="verify-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-black">DA</div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">Doserne</div>
            <div className="text-base font-black text-slate-900 leading-none">Academy</div>
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-8">
          <div className={"inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 " + ring}>
            {React.cloneElement(icon, { className: "w-8 h-8" })}
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-slate-400 mb-2">{kicker}</div>
          <h1 className="text-2xl font-black text-slate-900 leading-tight mb-6">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 pb-3 border-b border-slate-100 last:border-none last:pb-0">
      <dt className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</dt>
      <dd className={"text-sm font-bold text-slate-900 text-right " + (mono ? "font-mono" : "")}>{value}</dd>
    </div>
  );
}
